import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { BrandLogoWithWordmark } from '../BrandLogo';
import { getRoleLinks } from './navConfig';

const LANGUAGES = [
  { code: 'ru', label: 'RU' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();

  return (
    <div className="flex items-center gap-1 px-3 py-2">
      <Globe size={16} className="text-text-muted mr-1" strokeWidth={1.5} />
      {LANGUAGES.map(({ code, label }) => (
        <button
          key={code}
          onClick={() => i18n.changeLanguage(code)}
          title={label}
          className={`text-xs font-medium px-2 py-1 rounded transition-all ${
            i18n.language === code
              ? 'bg-primary text-white'
              : 'text-text-muted hover:text-primary hover:bg-primary/10'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

export const DesktopSidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const links = useMemo(() => getRoleLinks(user?.role, t), [user?.role, t]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="td-desktop-sidebar">
      <div className="p-6">
        <BrandLogoWithWordmark size={28} className="brand-logo-sidebar" />
        <p className="text-sm text-text-muted mt-1">{user?.name}</p>
      </div>
      <nav className="px-3 space-y-1">
        {links.map(({ to, icon: Icon, label }) => (
          <Link
            key={to}
            to={to}
            data-testid={`nav-link-${label.toLowerCase().replace(' ', '-')}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-[8px] transition-all duration-200 ${
              location.pathname === to
                ? 'bg-primary text-white shadow-sm font-semibold'
                : 'text-[#475569] hover:bg-[#f3f4f6] hover:text-primary'
            }`}
          >
            <Icon size={20} strokeWidth={1.5} />
            <span className="font-medium text-sm">{label}</span>
          </Link>
        ))}

        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-left text-text-secondary hover:bg-red-50 hover:text-red-600 transition-colors mt-4"
        >
          <LogOut size={20} strokeWidth={1.5} />
          <span className="font-medium text-sm">{t('nav.logout')}</span>
        </button>

        <div className="mt-4 border-t border-gray-100 pt-3">
          <LanguageSwitcher />
        </div>
      </nav>
    </aside>
  );
};
